import React, { useState, useEffect, ChangeEvent } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Chip,
  Collapse,
  Tooltip,
  Snackbar,
  Alert
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lock as LockIcon,
  Upload as UploadIcon,
  Download as DownloadIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { apiClient as api } from '../../api';

interface Level4Risk {
  id: number;
  tenant_id: number | null;
  level_3_risk_id: number;
  risk_id: string;
  name: string;
  code: string;
  description: string;
  descriptive_name: string;
  sort_order: number;
  is_active: boolean;
}

interface Level3Risk {
  id: number;
  tenant_id: number | null;
  level_2_risk_id: number;
  name: string;
  code: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  level4Risks?: Level4Risk[];
}

interface Level2Risk {
  id: number;
  tenant_id: number | null;
  level_1_risk_id: number;
  name: string;
  code: string;
  description: string;
  ai_prompt_definition: string;
  assessment_questions: any;
  sort_order: number;
  is_active: boolean;
  level1Risk?: Level1Risk;
  level3Risks?: Level3Risk[];
}

interface Level1Risk {
  id: number;
  tenant_id: number | null;
  name: string;
  code: string;
  description: string;
  sort_order: number;
  is_active: boolean;
  level2Risks?: Level2Risk[];
}

interface BulkUploadResult {
  total: number;
  created?: number;
  duplicates: number;
  failed: number;
  level1Created?: number;
  level2Created?: number;
  level3Created?: number;
  level4Created?: number;
  errors: Array<{
    row: number;
    code: string;
    error: string;
  }>;
}

const RiskTaxonomyManagement: React.FC = () => {
  const [level1Risks, setLevel1Risks] = useState<Level1Risk[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedL1Rows, setExpandedL1Rows] = useState<Set<number>>(new Set());
  const [expandedL2Rows, setExpandedL2Rows] = useState<Set<number>>(new Set());
  const [expandedL3Rows, setExpandedL3Rows] = useState<Set<number>>(new Set());
  const [showBulkUpload, setShowBulkUpload] = useState(false);
  const [bulkUploadLevel, setBulkUploadLevel] = useState<1 | 2 | 'full'>(1);
  const [bulkFile, setBulkFile] = useState<File | null>(null);
  const [uploadingBulk, setUploadingBulk] = useState(false);
  const [bulkResult, setBulkResult] = useState<BulkUploadResult | null>(null);

  // Snackbar state for notifications
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'warning' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // Dialog states
  const [openL1Dialog, setOpenL1Dialog] = useState(false);
  const [openL2Dialog, setOpenL2Dialog] = useState(false);
  const [editingL1, setEditingL1] = useState<Level1Risk | null>(null);
  const [editingL2, setEditingL2] = useState<Level2Risk | null>(null);
  const [savingL1, setSavingL1] = useState(false);
  const [savingL2, setSavingL2] = useState(false);

  // Confirmation dialog state
  const [confirmDialog, setConfirmDialog] = useState<{
    open: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    open: false,
    title: '',
    message: '',
    onConfirm: () => {}
  });

  // Form states
  const [l1Form, setL1Form] = useState({
    name: '',
    code: '',
    description: '',
    sort_order: 0
  });

  const [l2Form, setL2Form] = useState({
    level_1_risk_id: 0,
    name: '',
    code: '',
    description: '',
    ai_prompt_definition: '',
    sort_order: 0
  });

  useEffect(() => {
    loadLevel1Risks();
  }, []);

  // Helper function to show notifications
  const showNotification = (message: string, severity: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const loadLevel1Risks = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/risk-taxonomy/level1');
      setLevel1Risks(response.data.data);
    } catch (err: any) {
      showNotification('Failed to load risk taxonomy: ' + err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const toggleL1Row = (id: number) => {
    const newExpanded = new Set(expandedL1Rows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedL1Rows(newExpanded);
  };

  const toggleL2Row = (id: number) => {
    const newExpanded = new Set(expandedL2Rows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedL2Rows(newExpanded);
  };

  const toggleL3Row = (id: number) => {
    const newExpanded = new Set(expandedL3Rows);
    if (newExpanded.has(id)) {
      newExpanded.delete(id);
    } else {
      newExpanded.add(id);
    }
    setExpandedL3Rows(newExpanded);
  };

  // ========================================
  // Level 1 Risk Handlers
  // ========================================

  const handleOpenL1Dialog = (risk?: Level1Risk) => {
    if (risk) {
      setEditingL1(risk);
      setL1Form({
        name: risk.name,
        code: risk.code,
        description: risk.description,
        sort_order: risk.sort_order
      });
    } else {
      setEditingL1(null);
      // Auto-calculate next sort order based on existing risks
      const nextSortOrder = level1Risks.length > 0
        ? Math.max(...level1Risks.map(r => r.sort_order || 0)) + 1
        : 1;
      setL1Form({ name: '', code: '', description: '', sort_order: nextSortOrder });
    }
    setOpenL1Dialog(true);
  };

  const handleCloseL1Dialog = () => {
    setOpenL1Dialog(false);
    setEditingL1(null);
    setL1Form({ name: '', code: '', description: '', sort_order: 0 });
  };

  const handleSaveL1 = async () => {
    // Validation
    if (!l1Form.name.trim()) {
      showNotification('Name is required', 'error');
      return;
    }
    if (!l1Form.code.trim()) {
      showNotification('Code is required', 'error');
      return;
    }
    if (!l1Form.description.trim()) {
      showNotification('Description is required', 'error');
      return;
    }

    try {
      setSavingL1(true);

      // If sort_order is empty string, use auto-calculated value
      const formData = {
        ...l1Form,
        sort_order: l1Form.sort_order || (level1Risks.length > 0
          ? Math.max(...level1Risks.map(r => r.sort_order || 0)) + 1
          : 1)
      };

      if (editingL1) {
        // Update existing
        await api.put(`/api/risk-taxonomy/level1/${editingL1.id}`, formData);
      } else {
        // Create new
        await api.post('/api/risk-taxonomy/level1', formData);
      }

      handleCloseL1Dialog();
      loadLevel1Risks();
      showNotification(
        `Level 1 risk ${editingL1 ? 'updated' : 'created'} successfully!`,
        'success'
      );
    } catch (err: any) {
      showNotification(
        `Failed to ${editingL1 ? 'update' : 'create'} Level 1 risk: ${err.response?.data?.error || err.message}`,
        'error'
      );
    } finally {
      setSavingL1(false);
    }
  };

  const handleDeleteL1 = (id: number) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Level 1 Risk',
      message: 'Are you sure you want to delete this Level 1 risk? This will also delete all associated Level 2 risks.',
      onConfirm: async () => {
        try {
          await api.delete(`/api/risk-taxonomy/level1/${id}`);
          loadLevel1Risks();
          showNotification('Level 1 risk deleted successfully!', 'success');
        } catch (err: any) {
          showNotification(
            'Failed to delete Level 1 risk: ' + (err.response?.data?.error || err.message),
            'error'
          );
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      }
    });
  };

  // ========================================
  // Level 2 Risk Handlers
  // ========================================

  const handleOpenL2Dialog = (level1RiskId: number, risk?: Level2Risk) => {
    if (risk) {
      setEditingL2(risk);
      setL2Form({
        level_1_risk_id: risk.level_1_risk_id,
        name: risk.name,
        code: risk.code,
        description: risk.description,
        ai_prompt_definition: risk.ai_prompt_definition,
        sort_order: risk.sort_order
      });
    } else {
      setEditingL2(null);
      // Auto-calculate next sort order for this L1 category
      const parentRisk = level1Risks.find(r => r.id === level1RiskId);
      const existingL2Risks = parentRisk?.level2Risks || [];
      const nextSortOrder = existingL2Risks.length > 0
        ? Math.max(...existingL2Risks.map(r => r.sort_order || 0)) + 1
        : 1;
      setL2Form({
        level_1_risk_id: level1RiskId,
        name: '',
        code: '',
        description: '',
        ai_prompt_definition: '',
        sort_order: nextSortOrder
      });
    }
    setOpenL2Dialog(true);
  };

  const handleCloseL2Dialog = () => {
    setOpenL2Dialog(false);
    setEditingL2(null);
  };

  const handleSaveL2 = async () => {
    // Validation
    if (!l2Form.name.trim()) {
      showNotification('Name is required', 'error');
      return;
    }
    if (!l2Form.code.trim()) {
      showNotification('Code is required', 'error');
      return;
    }
    if (!l2Form.description.trim()) {
      showNotification('Description is required', 'error');
      return;
    }
    if (!l2Form.ai_prompt_definition.trim()) {
      showNotification('AI Prompt Definition is required', 'error');
      return;
    }

    try {
      setSavingL2(true);

      // If sort_order is empty string, use auto-calculated value
      const parentRisk = level1Risks.find(r => r.id === l2Form.level_1_risk_id);
      const existingL2Risks = parentRisk?.level2Risks || [];
      const formData = {
        ...l2Form,
        sort_order: l2Form.sort_order || (existingL2Risks.length > 0
          ? Math.max(...existingL2Risks.map(r => r.sort_order || 0)) + 1
          : 1)
      };

      if (editingL2) {
        await api.put(`/api/risk-taxonomy/level2/${editingL2.id}`, formData);
      } else {
        await api.post('/api/risk-taxonomy/level2', formData);
      }

      handleCloseL2Dialog();
      loadLevel1Risks();
      showNotification(
        `Level 2 risk ${editingL2 ? 'updated' : 'created'} successfully!`,
        'success'
      );
    } catch (err: any) {
      showNotification(
        `Failed to ${editingL2 ? 'update' : 'create'} Level 2 risk: ${err.response?.data?.error || err.message}`,
        'error'
      );
    } finally {
      setSavingL2(false);
    }
  };

  const handleDeleteL2 = (id: number) => {
    setConfirmDialog({
      open: true,
      title: 'Delete Level 2 Risk',
      message: 'Are you sure you want to delete this Level 2 risk? This action cannot be undone.',
      onConfirm: async () => {
        try {
          await api.delete(`/api/risk-taxonomy/level2/${id}`);
          loadLevel1Risks();
          showNotification('Level 2 risk deleted successfully!', 'success');
        } catch (err: any) {
          showNotification(
            'Failed to delete Level 2 risk: ' + (err.response?.data?.error || err.message),
            'error'
          );
        }
        setConfirmDialog({ ...confirmDialog, open: false });
      }
    });
  };

  const isGlobalRisk = (tenantId: number | null) => tenantId === null;

  const handleBulkUpload = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!bulkFile || uploadingBulk) return;

    try {
      setUploadingBulk(true);
      const formData = new FormData();
      formData.append('file', bulkFile);

      let endpoint = '/api/risk-taxonomy/bulk-upload-level1';
      if (bulkUploadLevel === 2) {
        endpoint = '/api/risk-taxonomy/bulk-upload-level2';
      } else if (bulkUploadLevel === 'full') {
        endpoint = '/api/risk-taxonomy/bulk-upload-full';
      }

      const response = await api.post(endpoint, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      if (response.data.success) {
        setBulkResult(response.data.data);
        setBulkFile(null);
        showNotification(response.data.message, 'success');
        loadLevel1Risks();
      } else {
        throw new Error(response.data.message || 'Upload failed');
      }
    } catch (error: any) {
      showNotification(error.response?.data?.message || 'Failed to upload file', 'error');
    } finally {
      setUploadingBulk(false);
    }
  };

  const downloadTemplate = () => {
    let csvContent = '';
    let fileName = '';

    if (bulkUploadLevel === 1) {
      csvContent = 'Code,Name,Description,Sort Order\n' +
        'CUSTOM-OP,Custom Operational Risk,Risk from internal processes,1\n' +
        'CUSTOM-TECH,Custom Technology Risk,Risk from technology failures,2';
      fileName = 'level1_risk_template.csv';
    } else if (bulkUploadLevel === 2) {
      csvContent = 'Level1Code,Code,Name,Description,AI Prompt Definition,Assessment Questions,Sort Order\n' +
        'OP,CUSTOM-PROC-01,Process Failure,Risk of process breakdown,Risks related to core process failures...,Question 1|Question 2,1\n' +
        'TECH,CUSTOM-SEC-01,Security Breach,Data security risk,Risks from security vulnerabilities...,Question 1,2';
      fileName = 'level2_risk_template.csv';
    } else {
      // Full Taxonomy (4 Levels)
      csvContent = 'Risk ID,L1 ID,L1,L2 ID,L2,L3 ID,L3,L4 ID,L4,L4 Full Name,Descriptive L4,L1 Definition,L2 Definition,L3 Definition,L4 Definition\n' +
        'R001,1,Operational Risk,1.1,Process Risk,1.1.1,Transaction Processing,1.1.1.1,Facilities and Utilities Resiliency,1.1.1.1 - Facilities and Utilities Resiliency,Business disruption due to facilities issues,The risk of operational disruption,The risk of process failures,The risk of transaction issues,Risk to business continuity from facilities\n' +
        'R002,1,Operational Risk,1.1,Process Risk,1.1.2,People/Workforce Resiliency,1.1.2.1,Workforce Resiliency,1.1.2.1 - People/Workforce Resiliency,Business disruption due to workforce issues,,,The risk of workforce unavailability,Risk to business from workforce disruption\n' +
        'R003,1,Operational Risk,1.2,Customer Risk,1.2.1,CFI Misconduct,1.2.2.1,CFI Misconduct,1.2.2.1 - CFI Misconduct,Material misconduct by CFI,,,The risk of customer misconduct,Insufficient controls for CFI behavior';
      fileName = 'full_taxonomy_template.csv';
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = fileName;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  if (loading) {
    return (
      <Card>
        <CardContent>
          <Typography>Loading risk taxonomy...</Typography>
        </CardContent>
      </Card>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h5" gutterBottom>
            Risk Taxonomy Management
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Manage global and tenant-specific risk categories (Level 1) and risk types (Level 2)
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<UploadIcon />}
            onClick={() => setShowBulkUpload(!showBulkUpload)}
          >
            Bulk Upload
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenL1Dialog()}
          >
            Add Level 1 Risk
          </Button>
        </Box>
      </Box>

      {showBulkUpload && (
        <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Bulk Upload Risks</Typography>
              <IconButton size="small" onClick={() => {
                setShowBulkUpload(false);
                setBulkResult(null);
                setBulkFile(null);
              }}>
                <CloseIcon />
              </IconButton>
            </Box>

            <Box sx={{ mb: 3, p: 2, bgcolor: '#e3f2fd', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                Upload Instructions
              </Typography>
              <Box component="ul" sx={{ mt: 1, pl: 2, fontSize: '0.875rem' }}>
                <li>Upload a CSV or Excel file with risk data</li>
                <li><strong>Level 1:</strong> Required columns: Code, Name, Description</li>
                <li><strong>Level 2:</strong> Required columns: Level1Code, Code, Name, Description, AI Prompt Definition</li>
                <li><strong>Full Taxonomy (4 Levels):</strong> Columns: Risk ID, L1 ID, L1, L2 ID, L2, L3 ID, L3, L4 ID, L4, L4 Full Name, Descriptive L4, L1-L4 Definitions</li>
                <li>Optional/empty fields are accepted - only Risk ID is required for Full Taxonomy</li>
              </Box>
              <Button
                variant="contained"
                size="small"
                startIcon={<DownloadIcon />}
                onClick={downloadTemplate}
                sx={{ mt: 2 }}
              >
                Download Template
              </Button>
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                select
                fullWidth
                value={bulkUploadLevel}
                onChange={(e) => {
                  const val = e.target.value;
                  setBulkUploadLevel(val === 'full' ? 'full' : Number(val) as 1 | 2);
                }}
                label="Risk Level"
                size="small"
              >
                <MenuItem value={1}>Level 1 (Categories)</MenuItem>
                <MenuItem value={2}>Level 2 (Types)</MenuItem>
                <MenuItem value="full">Full Taxonomy (4 Levels)</MenuItem>
              </TextField>
            </Box>

            <Box
              component="form"
              onSubmit={handleBulkUpload}
              sx={{
                border: '2px dashed #ccc',
                borderRadius: 1,
                p: 3,
                textAlign: 'center',
                mb: 2
              }}
            >
              <UploadIcon sx={{ fontSize: 48, color: '#999', mb: 2 }} />
              <input
                type="file"
                accept=".csv,.xlsx,.xls"
                onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                style={{ display: 'none' }}
                id="bulk-upload-risk"
              />
              <label htmlFor="bulk-upload-risk">
                <Button variant="outlined" component="span">
                  Choose File
                </Button>
              </label>
              {bulkFile && (
                <Typography variant="body2" sx={{ mt: 2 }}>
                  Selected: <strong>{bulkFile.name}</strong>
                </Typography>
              )}
            </Box>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              disabled={!bulkFile || uploadingBulk}
              onClick={handleBulkUpload}
              startIcon={uploadingBulk ? null : <UploadIcon />}
            >
              {uploadingBulk ? 'Uploading...' : 'Upload Risks'}
            </Button>

            {bulkResult && (
              <Box sx={{ mt: 3, p: 2, bgcolor: 'white', borderRadius: 1 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                  <Typography variant="subtitle2" fontWeight="bold">Upload Results</Typography>
                  <Button size="small" onClick={() => setBulkResult(null)}>Clear</Button>
                </Box>
                {/* Full Taxonomy Results - show per-level counts */}
                {(bulkResult.level1Created !== undefined || bulkResult.level2Created !== undefined ||
                  bulkResult.level3Created !== undefined || bulkResult.level4Created !== undefined) ? (
                  <>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2, mb: 2 }}>
                      <Paper sx={{ p: 2, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                        <Typography variant="h4" color="primary">{bulkResult.total}</Typography>
                        <Typography variant="caption">Total Rows</Typography>
                      </Paper>
                      <Paper sx={{ p: 2, bgcolor: '#fff3e0', textAlign: 'center' }}>
                        <Typography variant="h4" color="warning.main">{bulkResult.duplicates}</Typography>
                        <Typography variant="caption">Skipped</Typography>
                      </Paper>
                      <Paper sx={{ p: 2, bgcolor: '#ffebee', textAlign: 'center' }}>
                        <Typography variant="h4" color="error.main">{bulkResult.failed}</Typography>
                        <Typography variant="caption">Failed</Typography>
                      </Paper>
                    </Box>
                    <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>Records Created by Level:</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
                      <Paper sx={{ p: 2, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                        <Typography variant="h5" color="success.main">{bulkResult.level1Created || 0}</Typography>
                        <Typography variant="caption">Level 1</Typography>
                      </Paper>
                      <Paper sx={{ p: 2, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                        <Typography variant="h5" color="success.main">{bulkResult.level2Created || 0}</Typography>
                        <Typography variant="caption">Level 2</Typography>
                      </Paper>
                      <Paper sx={{ p: 2, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                        <Typography variant="h5" color="success.main">{bulkResult.level3Created || 0}</Typography>
                        <Typography variant="caption">Level 3</Typography>
                      </Paper>
                      <Paper sx={{ p: 2, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                        <Typography variant="h5" color="success.main">{bulkResult.level4Created || 0}</Typography>
                        <Typography variant="caption">Level 4</Typography>
                      </Paper>
                    </Box>
                  </>
                ) : (
                  /* Standard L1/L2 Results */
                  <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2, mb: 2 }}>
                    <Paper sx={{ p: 2, bgcolor: '#e3f2fd', textAlign: 'center' }}>
                      <Typography variant="h4" color="primary">{bulkResult.total}</Typography>
                      <Typography variant="caption">Total Rows</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, bgcolor: '#e8f5e9', textAlign: 'center' }}>
                      <Typography variant="h4" color="success.main">{bulkResult.created}</Typography>
                      <Typography variant="caption">Created</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, bgcolor: '#fff3e0', textAlign: 'center' }}>
                      <Typography variant="h4" color="warning.main">{bulkResult.duplicates}</Typography>
                      <Typography variant="caption">Duplicates</Typography>
                    </Paper>
                    <Paper sx={{ p: 2, bgcolor: '#ffebee', textAlign: 'center' }}>
                      <Typography variant="h4" color="error.main">{bulkResult.failed}</Typography>
                      <Typography variant="caption">Failed</Typography>
                    </Paper>
                  </Box>
                )}
                {bulkResult.errors.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Errors:</Typography>
                    <Box sx={{ maxHeight: 200, overflowY: 'auto' }}>
                      {bulkResult.errors.map((err, idx) => (
                        <Paper key={idx} sx={{ p: 1, mb: 1, bgcolor: '#ffebee', fontSize: '0.75rem' }}>
                          <strong>Row {err.row}:</strong> {err.error}
                          {err.code && <span style={{ color: '#666' }}> ({err.code})</span>}
                        </Paper>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      <Alert severity="info" sx={{ mb: 2 }}>
        <Typography variant="body2" fontWeight="bold">
          Global vs Tenant-Specific Risks:
        </Typography>
        <Typography variant="caption">
          • <strong>Global Risks</strong> (with 🔒) are system-wide and cannot be edited or deleted
          <br />
          • <strong>Tenant-Specific Risks</strong> are custom risks you create for your organization
        </Typography>
      </Alert>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {level1Risks.map((l1) => (
          <Card key={l1.id} variant="outlined" sx={{ border: '2px solid #e0e0e0' }}>
            <CardContent sx={{ p: 0 }}>
              {/* Level 1 Header */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  p: 2,
                  bgcolor: '#fafafa',
                  borderBottom: expandedL1Rows.has(l1.id) ? '1px solid #e0e0e0' : 'none',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: '#f0f0f0' }
                }}
                onClick={() => toggleL1Row(l1.id)}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flex: 1 }}>
                  <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                    {expandedL1Rows.has(l1.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>

                  <Chip
                    label="L1"
                    size="small"
                    sx={{
                      bgcolor: '#1976d2',
                      color: 'white',
                      fontWeight: 'bold',
                      fontSize: '0.75rem',
                      minWidth: '40px'
                    }}
                  />

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Typography variant="h6" fontWeight="600" color="primary">
                        {l1.code}
                      </Typography>
                      <Typography variant="h6" fontWeight="500">
                        {l1.name}
                      </Typography>
                      {isGlobalRisk(l1.tenant_id) ? (
                        <Chip icon={<LockIcon />} label="Global" size="small" color="default" />
                      ) : (
                        <Chip label="Custom" size="small" color="primary" />
                      )}
                      {l1.level2Risks && l1.level2Risks.length > 0 && (
                        <Chip
                          label={`${l1.level2Risks.length} Risk Types`}
                          size="small"
                          sx={{ bgcolor: '#fff3e0', color: '#ff9800', fontWeight: 'bold' }}
                        />
                      )}
                    </Box>
                    <Typography variant="body2" color="text.secondary">
                      {l1.description}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                  {!isGlobalRisk(l1.tenant_id) && (
                    <>
                      <Tooltip title="Edit Level 1 Risk">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleOpenL1Dialog(l1);
                          }}
                          sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e0e0e0' } }}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Level 1 Risk">
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteL1(l1.id);
                          }}
                          sx={{ bgcolor: '#ffebee', color: '#d32f2f', '&:hover': { bgcolor: '#ffcdd2' } }}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </>
                  )}
                </Box>
              </Box>

              {/* Level 2 Risks (Expanded) */}
              <Collapse in={expandedL1Rows.has(l1.id)}>
                <Box sx={{ p: 2, bgcolor: '#f9f9f9' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                    <Typography variant="subtitle1" fontWeight="600" color="primary">
                      Level 2 Risk Types
                    </Typography>
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AddIcon />}
                      onClick={() => handleOpenL2Dialog(l1.id)}
                    >
                      Add Level 2 Risk
                    </Button>
                  </Box>

                          {l1.level2Risks && l1.level2Risks.length > 0 ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                              {l1.level2Risks.map((l2, index) => (
                                <Card key={l2.id} variant="outlined" sx={{ bgcolor: 'white', border: '1px solid #e0e0e0' }}>
                                  {/* L2 Header - Clickable */}
                                  <Box
                                    sx={{
                                      display: 'flex',
                                      justifyContent: 'space-between',
                                      alignItems: 'center',
                                      p: 1.5,
                                      cursor: l2.level3Risks && l2.level3Risks.length > 0 ? 'pointer' : 'default',
                                      '&:hover': l2.level3Risks && l2.level3Risks.length > 0 ? { bgcolor: '#f5f5f5' } : {}
                                    }}
                                    onClick={() => l2.level3Risks && l2.level3Risks.length > 0 && toggleL2Row(l2.id)}
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                      {l2.level3Risks && l2.level3Risks.length > 0 && (
                                        <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                                          {expandedL2Rows.has(l2.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                        </IconButton>
                                      )}
                                      <Chip
                                        label="L2"
                                        size="small"
                                        sx={{ bgcolor: '#ff9800', color: 'white', fontWeight: 'bold', fontSize: '0.7rem', height: '24px' }}
                                      />
                                      <Typography variant="body2" fontWeight="600" color="primary">{l2.code}</Typography>
                                      <Typography variant="body1" fontWeight="500">{l2.name}</Typography>
                                      {isGlobalRisk(l2.tenant_id) ? (
                                        <Chip icon={<LockIcon />} label="Global" size="small" color="default" />
                                      ) : (
                                        <Chip label="Custom" size="small" color="primary" />
                                      )}
                                      {l2.level3Risks && l2.level3Risks.length > 0 && (
                                        <Chip label={`${l2.level3Risks.length} L3`} size="small" sx={{ bgcolor: '#e8f5e9', color: '#4caf50', fontWeight: 'bold' }} />
                                      )}
                                    </Box>
                                    <Box sx={{ display: 'flex', gap: 0.5 }} onClick={(e) => e.stopPropagation()}>
                                      {!isGlobalRisk(l2.tenant_id) && (
                                        <>
                                          <Tooltip title="Edit Level 2 Risk">
                                            <IconButton size="small" onClick={() => handleOpenL2Dialog(l1.id, l2)} sx={{ bgcolor: '#f5f5f5', '&:hover': { bgcolor: '#e0e0e0' } }}>
                                              <EditIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                          <Tooltip title="Delete Level 2 Risk">
                                            <IconButton size="small" onClick={() => handleDeleteL2(l2.id)} sx={{ bgcolor: '#ffebee', color: '#d32f2f', '&:hover': { bgcolor: '#ffcdd2' } }}>
                                              <DeleteIcon fontSize="small" />
                                            </IconButton>
                                          </Tooltip>
                                        </>
                                      )}
                                    </Box>
                                  </Box>

                                  {/* L3 Risks (Expanded under L2) */}
                                  <Collapse in={expandedL2Rows.has(l2.id)}>
                                    <Box sx={{ pl: 4, pr: 2, pb: 2, bgcolor: '#fafafa' }}>
                                      {l2.level3Risks && l2.level3Risks.length > 0 ? (
                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                          {l2.level3Risks.map((l3) => (
                                            <Card key={l3.id} variant="outlined" sx={{ bgcolor: 'white', border: '1px solid #e8e8e8' }}>
                                              {/* L3 Header - Clickable */}
                                              <Box
                                                sx={{
                                                  display: 'flex',
                                                  justifyContent: 'space-between',
                                                  alignItems: 'center',
                                                  p: 1.5,
                                                  cursor: l3.level4Risks && l3.level4Risks.length > 0 ? 'pointer' : 'default',
                                                  '&:hover': l3.level4Risks && l3.level4Risks.length > 0 ? { bgcolor: '#f5f5f5' } : {}
                                                }}
                                                onClick={() => l3.level4Risks && l3.level4Risks.length > 0 && toggleL3Row(l3.id)}
                                              >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                                                  {l3.level4Risks && l3.level4Risks.length > 0 && (
                                                    <IconButton size="small" sx={{ pointerEvents: 'none' }}>
                                                      {expandedL3Rows.has(l3.id) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                  )}
                                                  <Chip label="L3" size="small" sx={{ bgcolor: '#4caf50', color: 'white', fontWeight: 'bold', fontSize: '0.7rem', height: '22px' }} />
                                                  <Typography variant="body2" fontWeight="600" color="success.main">{l3.code}</Typography>
                                                  <Typography variant="body2" fontWeight="500">{l3.name}</Typography>
                                                  {l3.level4Risks && l3.level4Risks.length > 0 && (
                                                    <Chip label={`${l3.level4Risks.length} L4`} size="small" sx={{ bgcolor: '#e3f2fd', color: '#1976d2', fontWeight: 'bold' }} />
                                                  )}
                                                </Box>
                                              </Box>

                                              {/* L4 Risks (Expanded under L3) */}
                                              <Collapse in={expandedL3Rows.has(l3.id)}>
                                                <Box sx={{ pl: 4, pr: 2, pb: 1.5, bgcolor: '#f5f5f5' }}>
                                                  {l3.level4Risks && l3.level4Risks.length > 0 ? (
                                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                                                      {l3.level4Risks.map((l4) => (
                                                        <Box key={l4.id} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, p: 1, bgcolor: 'white', borderRadius: 1, border: '1px solid #e0e0e0' }}>
                                                          <Chip label="L4" size="small" sx={{ bgcolor: '#9c27b0', color: 'white', fontWeight: 'bold', fontSize: '0.65rem', height: '20px' }} />
                                                          <Typography variant="caption" fontWeight="600" color="secondary">{l4.risk_id}</Typography>
                                                          <Typography variant="body2">{l4.name}</Typography>
                                                          {l4.descriptive_name && l4.descriptive_name !== l4.name && (
                                                            <Typography variant="caption" color="text.secondary">({l4.descriptive_name})</Typography>
                                                          )}
                                                        </Box>
                                                      ))}
                                                    </Box>
                                                  ) : (
                                                    <Typography variant="caption" color="text.secondary">No Level 4 risks</Typography>
                                                  )}
                                                </Box>
                                              </Collapse>
                                            </Card>
                                          ))}
                                        </Box>
                                      ) : (
                                        <Typography variant="caption" color="text.secondary">No Level 3 risks</Typography>
                                      )}
                                    </Box>
                                  </Collapse>
                                </Card>
                              ))}
                            </Box>
                  ) : (
                    <Alert severity="info" sx={{ m: 2 }}>
                      No Level 2 risks defined yet. Click "Add Level 2 Risk" to create one.
                    </Alert>
                  )}
                </Box>
              </Collapse>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Level 1 Dialog */}
      <Dialog open={openL1Dialog} onClose={handleCloseL1Dialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingL1 ? 'Edit Level 1 Risk' : 'Add Level 1 Risk'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Risk Code"
              value={l1Form.code}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setL1Form({ ...l1Form, code: e.target.value })}
              required
              fullWidth
              helperText="Unique code (e.g., CUSTOM-OP)"
            />
            <TextField
              label="Risk Name"
              value={l1Form.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setL1Form({ ...l1Form, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={l1Form.description}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setL1Form({ ...l1Form, description: e.target.value })}
              required
              fullWidth
              multiline
              rows={3}
            />
            <TextField
              label="Sort Order"
              type="number"
              value={l1Form.sort_order}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value === '' ? '' : parseInt(e.target.value);
                setL1Form({ ...l1Form, sort_order: value as number });
              }}
              fullWidth
              helperText="Lower numbers appear first"
              placeholder="Auto-assigned if empty"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseL1Dialog} disabled={savingL1}>Cancel</Button>
          <Button
            onClick={handleSaveL1}
            variant="contained"
            disabled={savingL1}
          >
            {savingL1 ? 'Saving...' : (editingL1 ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Level 2 Dialog */}
      <Dialog open={openL2Dialog} onClose={handleCloseL2Dialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingL2 ? 'Edit Level 2 Risk' : 'Add Level 2 Risk'}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <TextField
              label="Risk Code"
              value={l2Form.code}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setL2Form({ ...l2Form, code: e.target.value })}
              required
              fullWidth
              helperText="Unique code (e.g., CUSTOM-PROC-01)"
            />
            <TextField
              label="Risk Name"
              value={l2Form.name}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setL2Form({ ...l2Form, name: e.target.value })}
              required
              fullWidth
            />
            <TextField
              label="Description"
              value={l2Form.description}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setL2Form({ ...l2Form, description: e.target.value })}
              required
              fullWidth
              multiline
              rows={2}
            />
            <TextField
              label="AI Prompt Definition"
              value={l2Form.ai_prompt_definition}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setL2Form({ ...l2Form, ai_prompt_definition: e.target.value })}
              required
              fullWidth
              multiline
              rows={4}
              helperText="Detailed definition used by AI to identify this risk type"
            />
            <TextField
              label="Sort Order"
              type="number"
              value={l2Form.sort_order}
              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                const value = e.target.value === '' ? '' : parseInt(e.target.value);
                setL2Form({ ...l2Form, sort_order: value as number });
              }}
              fullWidth
              helperText="Lower numbers appear first"
              placeholder="Auto-assigned if empty"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseL2Dialog} disabled={savingL2}>Cancel</Button>
          <Button
            onClick={handleSaveL2}
            variant="contained"
            disabled={savingL2}
          >
            {savingL2 ? 'Saving...' : (editingL2 ? 'Update' : 'Create')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirmation Dialog */}
      <Dialog
        open={confirmDialog.open}
        onClose={() => setConfirmDialog({ ...confirmDialog, open: false })}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box
              sx={{
                width: 40,
                height: 40,
                borderRadius: '50%',
                bgcolor: 'error.light',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}
            >
              <DeleteIcon sx={{ color: 'error.main' }} />
            </Box>
            <Typography variant="h6">{confirmDialog.title}</Typography>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>{confirmDialog.message}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            onClick={() => setConfirmDialog({ ...confirmDialog, open: false })}
            variant="outlined"
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={confirmDialog.onConfirm}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default RiskTaxonomyManagement;
